import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, Mail, ChevronDown, ArrowRight } from 'lucide-react';
import { artistInfo, exhibitions } from '../mockData';
import { seriesList } from '../data/seriesData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Navigation from '../components/Navigation';

const Home = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Handle hash scrolling when arriving from another page
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToSeries = (slug) => {
    navigate(`/series/${slug}`);
    window.scrollTo(0, 0);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setFormSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormSubmitted(false), 5000);
      } else {
        if (data.detail && Array.isArray(data.detail)) {
          setFormError(data.detail.map(err => err.msg).join(', '));
        } else {
          setFormError(data.detail || 'Failed to send message. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Find a hero image - use first work from Epic Fury (most recent series)
  const heroImage = seriesList
    .slice()
    .reverse()
    .find(s => s.works.length > 0)?.works[0]?.image;

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-neutral-900 via-neutral-800 to-stone-900">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              {/* Bertolt Brecht Quote */}
              <div className="mb-8">
                <p className="text-neutral-300 italic leading-relaxed whitespace-pre-line text-lg mb-2">
                  "{artistInfo.quote}"
                </p>
                <p className="text-neutral-400 text-sm">
                  (Brecht)
                </p>
              </div>

              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                {artistInfo.statement}
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button
                  data-testid="hero-view-works-btn"
                  onClick={() => scrollToSection('work')}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg transition-all duration-300"
                >
                  View Works
                </Button>
                <Button
                  data-testid="hero-contact-btn"
                  onClick={() => scrollToSection('contact')}
                  variant="outline"
                  className="border-neutral-400 text-neutral-50 hover:bg-neutral-800 px-8 py-6 text-lg transition-all duration-300"
                >
                  Get in Touch
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[3/4] bg-gradient-to-br from-stone-700 to-neutral-800 rounded-lg overflow-hidden shadow-2xl">
                {heroImage && (
                  <img
                    src={heroImage}
                    alt="Featured artwork"
                    className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-500"
                  />
                )}
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-red-600/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
        <div className="container mx-auto max-w-6xl mt-12 flex justify-center">
          <button
            onClick={() => scrollToSection('work')}
            className="animate-bounce text-neutral-400 hover:text-neutral-50 transition-colors"
            aria-label="Scroll down"
          >
            <ChevronDown size={32} />
          </button>
        </div>
      </section>

      {/* Series / Bodies of Work Section */}
      <section id="work" className="py-20 px-6 bg-neutral-50">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-16 max-w-3xl">
            <p className="text-red-500 font-medium mb-3 tracking-wide uppercase text-xs font-mono">
              Bodies of Work
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Series
            </h2>
            <p className="text-lg text-neutral-600 leading-relaxed">
              Six discrete bodies of work, listed chronologically. Each series represents a sustained engagement with specific political and institutional concerns.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {seriesList.map((series) => {
              const coverImage = series.works[0]?.image;
              const workCount = series.works.length;
              return (
                <button
                  key={series.slug}
                  data-testid={`series-card-${series.slug}`}
                  onClick={() => goToSeries(series.slug)}
                  className="group text-left bg-white border border-neutral-200 hover:border-red-600 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-xl"
                >
                  <div className="aspect-[4/3] bg-neutral-100 overflow-hidden relative">
                    {coverImage ? (
                      <img
                        src={coverImage}
                        alt={series.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neutral-100 to-neutral-200">
                        <p className="text-neutral-400 text-sm font-mono">Images coming soon</p>
                      </div>
                    )}
                    <div className="absolute top-3 left-3 bg-neutral-900/80 backdrop-blur-sm text-neutral-100 px-2 py-1 text-xs font-mono">
                      {String(series.order).padStart(2, '0')}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-neutral-900 group-hover:text-red-600 transition-colors mb-2">
                      {series.title}
                    </h3>
                    <p className="text-sm text-neutral-600 leading-relaxed line-clamp-3 mb-4">
                      {series.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-neutral-500 font-mono">
                        {workCount > 0 ? `${workCount} ${workCount === 1 ? 'piece' : 'pieces'}` : 'Forthcoming'}
                      </span>
                      <span className="text-red-600 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 text-sm font-medium">
                        View <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-neutral-100">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-5 gap-12 items-start">
            <div className="md:col-span-2">
              <div className="aspect-[3/4] bg-gradient-to-br from-neutral-300 to-stone-400 rounded-lg overflow-hidden shadow-xl sticky top-24">
                <img
                  src="https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&q=80"
                  alt={artistInfo.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
                About the Artist
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-neutral-700 leading-relaxed mb-6 text-lg">
                  {artistInfo.bio}
                </p>
                <div className="bg-red-50 border-l-4 border-red-600 p-6 my-8">
                  <p className="text-neutral-800 italic leading-relaxed">
                    "{artistInfo.statement}"
                  </p>
                </div>
                <div className="mt-8">
                  <h3 className="text-2xl font-bold text-neutral-900 mb-4">Recognition & Press</h3>
                  <ul className="space-y-3 text-neutral-700">
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Featured in Artforum's "Artists to Watch" 2024</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Recipient of the Contemporary Political Art Prize 2023</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Works held in permanent collections at MoMA and Tate Modern</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exhibitions Section */}
      <section id="exhibitions" className="py-20 px-6 bg-neutral-900">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-50 mb-4">
              Exhibitions
            </h2>
            <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
              Recent solo and group exhibitions showcasing politically engaged contemporary art.
            </p>
          </div>

          <div className="space-y-8">
            {exhibitions.map((exhibition) => (
              <div
                key={exhibition.id}
                data-testid={`exhibition-${exhibition.id}`}
                className="bg-neutral-800/50 border border-neutral-700 hover:bg-neutral-800 transition-all duration-300 p-8"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 bg-red-600/20 text-red-400 text-sm font-medium rounded-full">
                        {exhibition.type}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-neutral-50 mb-2">
                      {exhibition.title}
                    </h3>
                    <p className="text-neutral-300 mb-2">{exhibition.venue}</p>
                    <p className="text-neutral-500 text-sm">{exhibition.date}</p>
                  </div>
                </div>
                <p className="text-neutral-400 leading-relaxed">{exhibition.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-neutral-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Get in Touch
            </h2>
            <p className="text-lg text-neutral-600">
              Interested in commissions, exhibitions, or collaborations? Let's talk.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-neutral-900 mb-6">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-neutral-700">
                  <Mail className="text-red-600" size={20} />
                  <a
                    href={`mailto:${artistInfo.email}`}
                    data-testid="contact-email-link"
                    className="hover:text-red-600 transition-colors"
                  >
                    {artistInfo.email}
                  </a>
                </div>
                <div className="flex items-center gap-4 text-neutral-700">
                  <Instagram className="text-red-600" size={20} />
                  <a
                    href={`https://instagram.com/${artistInfo.social.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-red-600 transition-colors"
                  >
                    {artistInfo.social.instagram}
                  </a>
                </div>
              </div>
              <div className="mt-8 p-6 bg-red-50 border-l-4 border-red-600 rounded">
                <p className="text-neutral-800 leading-relaxed">
                  <strong>For gallery inquiries and acquisition:</strong><br />
                  Please include details about your interest and I'll respond within 48 hours.
                </p>
              </div>
            </div>

            <div>
              <form
                onSubmit={handleFormSubmit}
                data-testid="contact-form"
                className="space-y-6"
              >
                {formError && (
                  <div data-testid="form-error-message" className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">{formError}</p>
                  </div>
                )}
                {formSubmitted && (
                  <div data-testid="form-success-message" className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm font-medium">
                      Thank you! Your message has been sent successfully.
                    </p>
                  </div>
                )}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-900 mb-2">
                    Name
                  </label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full"
                    placeholder="Your name"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-900 mb-2">
                    Email
                  </label>
                  <Input
                    id="email"
                    data-testid="contact-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full"
                    placeholder="your@email.com"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-neutral-900 mb-2">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={5}
                    className="w-full"
                    placeholder="Tell me about your inquiry..."
                    disabled={isSubmitting}
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg transition-all duration-300"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-neutral-400 py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h3 className="text-2xl font-bold text-neutral-50 mb-2">{artistInfo.name}</h3>
              <p className="text-neutral-500">Contemporary Political Artist</p>
            </div>
            <div className="flex gap-6">
              <a
                href={`mailto:${artistInfo.email}`}
                className="hover:text-red-500 transition-colors"
                aria-label="Email"
              >
                <Mail size={24} />
              </a>
              <a
                href={`https://instagram.com/${artistInfo.social.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-red-500 transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={24} />
              </a>
            </div>
          </div>
          <div className="border-t border-neutral-800 mt-8 pt-8 text-center text-sm text-neutral-600">
            <p>© {new Date().getFullYear()} {artistInfo.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
